import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
  signIn as awsSignIn,
  signOut as awsSignOut,
  getCurrentUser,
  signInWithRedirect,
  confirmSignIn,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { amplifyConfig } from '../amplifyConfig';
import { installAuthFetch } from '../api/authFetch';

Amplify.configure(amplifyConfig);
// Every `/api/` request carries the Cognito ID token from here on.
installAuthFetch();

type AuthContextType = {
  user: any | null;
  groups: string[];
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
};

export const AuthContext = createContext<AuthContextType | null>(null);

const extractGroups = (payload: any): string[] => {
  const g = payload?.['cognito:groups'];
  if (Array.isArray(g)) return g as string[];
  if (typeof g === 'string') return [g];
  return [];
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        const { tokens } = await fetchAuthSession();
        const userGroups = extractGroups(tokens?.idToken?.payload);
        setUser(currentUser);
        setGroups(userGroups);
      } catch {
        setUser(null);
        setGroups([]);
      }
      setLoading(false);
    };

    checkUser();

    const unsubscribe = Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signedIn':
          sessionStorage.setItem('isJustLoggedIn', 'true');
          checkUser();
          break;
        case 'signedOut':
          setUser(null);
          setGroups([]);
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && user.signInDetails?.loginId) {
      const email = user.signInDetails.loginId;
      
      fetch('/api/user-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, groups }),
      }).catch(err => console.error('Failed to record user activity', err));
    }
  }, [user, groups]);

  const signIn = async (email: string, password: string) => {
    try {
      const { isSignedIn, nextStep } = await awsSignIn({
        username: email,
        password,
      });
      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        const { tokens } = await fetchAuthSession();
        const userGroups = extractGroups(tokens?.idToken?.payload);
        setUser(currentUser);
        setGroups(userGroups);
      }
      return { isSignedIn, nextStep };
    } catch (error) {
      setUser(null);
      setGroups([]);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in with Google', error);
    }
  };

  const confirmNewPassword = async (newPassword: string) => {
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const currentUser = await getCurrentUser();
      const { tokens } = await fetchAuthSession();
      const userGroups = extractGroups(tokens?.idToken?.payload);
      setUser(currentUser);
      setGroups(userGroups);
    } catch (error) {
      console.error('Error confirming new password', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await awsSignOut();
      setUser(null);
      setGroups([]);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const value: AuthContextType = {
    user,
    groups,
    loading,
    isAuthenticated: !!user,
    signIn,
    signOut,
    signInWithGoogle,
    confirmNewPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
